import { useEffect } from 'react';
import { useQueryClient } from 'react-query';

/**
 * Keeps the customer's entitlements (the 'getUserSubscription' query = GET /company/details) in step
 * with the package WITHOUT a reload, the way the Remote does.
 *
 * Triggers (all quiet: no toast, no spinner, no refetch while the tab is hidden):
 *   - the tab gets focus / becomes visible: at most once per FOCUS_THROTTLE_MS (60 s);
 *   - a protected request answered 402 `feature_disabled` (axios dispatches FEATURE_RECHECK_EVENT):
 *     refetch once, at most once per FEATURE_402_THROTTLE_MS (10 s). While hidden it is remembered
 *     and served when the tab is visible again.
 * Not touched: the subscription-expiry 402 path (PLAN_RECHECK_EVENT) and the no-package state.
 *
 * It does NOT go through useQuery's refetch: the query's onError shows a "Fetching Data" toast, and a
 * background refresh that fails (offline) must stay silent. It calls the same request itself and writes
 * the answer into the cache with setQueryData; react-query's structural sharing keeps the object
 * identical when nothing changed, so an unchanged package re-renders nothing. A changed one flows
 * through the router's existing effect into redux, exactly like a first load.
 */
export const FEATURE_RECHECK_EVENT = 'pp:feature-recheck';
export const FOCUS_THROTTLE_MS = 60_000;
export const FEATURE_402_THROTTLE_MS = 10_000;

export function createEntitlementsRefresher({
  fetchDetails,
  setDetails,
  now = () => Date.now(),
  isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
}) {
  // The details were fetched when the app opened: the first focus is not "stale" yet.
  let lastRefreshAt = now();
  let inflight = false;
  let pendingFeature402 = false;

  const run = async () => {
    if (inflight) return;
    inflight = true;
    lastRefreshAt = now();
    try {
      const data = await fetchDetails();
      if (data) setDetails(data);
    } catch (_) {
      // silent by design: the next trigger tries again
    } finally {
      inflight = false;
    }
  };

  return {
    onFocus() {
      if (!isVisible()) return;
      if (pendingFeature402 && now() - lastRefreshAt >= FEATURE_402_THROTTLE_MS) {
        pendingFeature402 = false;
        return run();
      }
      if (now() - lastRefreshAt < FOCUS_THROTTLE_MS) return;
      return run();
    },
    onFeature402() {
      if (!isVisible()) {
        pendingFeature402 = true;
        return;
      }
      if (now() - lastRefreshAt < FEATURE_402_THROTTLE_MS) return;
      return run();
    },
  };
}

/** Mounted once, inside the authenticated shell (CompanyDetailsRouter). */
export function useEntitlementsRefresh(fetchDetails) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refresher = createEntitlementsRefresher({
      fetchDetails,
      setDetails: (data) => queryClient.setQueryData('getUserSubscription', data),
    });
    const onFocus = () => refresher.onFocus();
    const onVisibility = () => { if (document.visibilityState === 'visible') refresher.onFocus(); };
    const on402 = () => refresher.onFeature402();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(FEATURE_RECHECK_EVENT, on402);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(FEATURE_RECHECK_EVENT, on402);
    };
  }, [queryClient, fetchDetails]);
}
